import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/services/auth/AuthProvider';
import { getProfileRole } from '@/services/auth/auth.service';
import { homeownerMembersService } from '@/services/supabase/homeownerMembers.service';
import { SidebarLogo } from './components/SidebarLogo';
import { SidebarPropertySelector } from './components/SidebarPropertySelector';
import { SidebarNavGroup } from './components/SidebarNavGroup';
import { SidebarBottomBar } from './components/SidebarBottomBar';
import { getSidebarConfig } from './constants';
import type { SidebarProps, UserRole } from './types';
import styles from './Sidebar.module.css';

export const Sidebar: React.FC<SidebarProps> = ({
  role: propRole,
  isOpen = false,
  onClose,
  className,
  variant = 'default',
}) => {
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [userRole, setUserRole] = useState<UserRole>(propRole);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  // Sync userRole when propRole changes
  useEffect(() => {
    if (propRole) {
      setUserRole(propRole);
    } else {
      // Fetch user role if not provided
      const fetchRole = async () => {
        const role = await getProfileRole();
        if (role === 'technician') {
          setUserRole('technician');
        } else if (role === 'admin') {
          setUserRole('admin');
        } else {
          setUserRole('homeowner');
        }
      };
      fetchRole();
    }
  }, [propRole]);

  // Get sidebar config based on role
  const config = getSidebarConfig(userRole);

  // #2884 — the Members management link is owner-only. Hidden while unknown so a
  // household member never sees it flash in — but for OWNERS it must appear
  // without a hard refresh. The old version cached under a user-agnostic key
  // with default=false: with the 24h offline-first cache, a stale "false"
  // (previous session/user) stuck until a manual reload, hiding the link from
  // owners — the exact mobile symptom from the 2026-09-25 smoke test. Fixes:
  // per-user cache key (no cross-session reuse) + refetch on every mount
  // (mobile PWA resume remounts the sidebar, so resume re-checks too).
  const { user } = useAuth();
  const { data: isPrimaryHomeowner = false } = useQuery({
    queryKey: ['isPrimaryHomeowner', user?.id ?? 'anon'],
    queryFn: () => homeownerMembersService.isPrimaryHomeowner(),
    enabled: userRole === 'homeowner' && !!user?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
  });

  // Initialize expanded sections based on config
  useEffect(() => {
    const initialExpanded: Record<string, boolean> = {};
    config.navGroups.forEach((group) => {
      initialExpanded[group.id] = group.defaultExpanded ?? true;
    });
    setExpandedSections(initialExpanded);
  }, [config]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  }, []);

  const handleNavItemClick = useCallback(() => {
    // Close sidebar on mobile when navigating
    onClose?.();
  }, [onClose]);

  const handleSignOut = useCallback(async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await logout();
    } catch (error) {
      console.error('Failed to logout', error);
    } finally {
      setIsLoggingOut(false);
    }
  }, [isLoggingOut, logout]);

  // Determine variant class
  const variantClass = variant === 'ai' ? styles.sidebarAi : '';

  return (
    <aside
      className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : ''} ${variantClass} ${className || ''}`}
    >
      {/* Container for all content */}
      <div className={styles.container}>
        {/* Logo */}
        {config.showLogo && <SidebarLogo />}

        {/* Property Selector */}
        {config.showPropertySelector && (
          <div className={styles.propertySelectorWrapper}>
            <SidebarPropertySelector />
          </div>
        )}

        {/* Navigation */}
        <nav className={styles.nav}>
          {config.navGroups.map((group) => {
            // #2884 — hide the owner-only Members link from household members.
            const items = isPrimaryHomeowner
              ? group.items
              : group.items.filter((item) => item.id !== 'members');
            if (items.length === 0) return null;
            return (
              <SidebarNavGroup
                key={group.id}
                group={{ ...group, items }}
                isExpanded={expandedSections[group.id] ?? true}
                onToggle={() => toggleSection(group.id)}
                onItemClick={handleNavItemClick}
              />
            );
          })}
        </nav>

        {/* Bottom Bar */}
        <SidebarBottomBar
          onSignOut={handleSignOut}
          isSigningOut={isLoggingOut}
        />
      </div>
    </aside>
  );
};
